import { getCommandById, executeCommand } from '../../command-palette/registry/commandRegistry';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { runShellAction } from '../../../layout/menu/appMenuShellActions';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { chordToElectronAccelerator } from '../model/electronAccelerator';
import { defaultKeybindings } from '../model/defaultKeybindings';
import { platform } from '../model/platform';
import type {
  ChordModifier,
  KeybindingCommandId,
  Platform,
} from '../model/keybindingTypes';
import {
  isCommandAllowed,
  keybindingCommandIdFor,
  type AppCommandSource,
} from './commandGate';
import { getEffectiveKeymap } from './effectiveKeymap';
import { resolveFocus } from './focusPolicy';
import {
  resolveActiveTarget,
  type ResolvedKeybindingTarget,
} from './targetRegistry';

export type RunAppCommandOptions = {
  readonly source: AppCommandSource;
  readonly accelerator?: string;
};

export type RunAppCommandResult = { readonly handled: boolean };

export type AppCommandRunnerDependencies = {
  readonly getActiveTabId: () => string | null;
  readonly getTarget: (
    commandId: KeybindingCommandId,
    activeTabId: string | null,
  ) => ResolvedKeybindingTarget | null;
  readonly isAllowed: (
    id: string,
    source: AppCommandSource,
    target: ResolvedKeybindingTarget | null,
  ) => boolean;
  readonly hasFocus: (
    commandId: KeybindingCommandId,
    target: ResolvedKeybindingTarget | null,
    accelerator?: string,
  ) => boolean;
  readonly isCurrentAccelerator: (commandId: KeybindingCommandId, accelerator: string) => boolean;
  readonly runShell: (id: string) => boolean;
  readonly runPalette: (id: string) => boolean;
};

function executionId(id: string, commandId: KeybindingCommandId | null): string {
  if (!commandId || id !== commandId) return id;
  const definition = defaultKeybindings[commandId];
  return definition.menuCommand ?? definition.paletteCommandId ?? id;
}

function normalizedAccelerator(value: string, targetPlatform: Platform): string {
  const aliases: Readonly<Record<string, string>> = {
    cmdorctrl: targetPlatform === 'mac' ? 'command' : 'control',
    commandorcontrol: targetPlatform === 'mac' ? 'command' : 'control',
    cmd: 'command', ctrl: 'control', option: 'alt', esc: 'escape',
    left: 'arrowleft', right: 'arrowright', up: 'arrowup', down: 'arrowdown',
  };
  const tokens = value.split('+').map((part) => {
    const token = part.trim().toLowerCase();
    return aliases[token] ?? token;
  });
  const key = tokens.pop() ?? '';
  return `${tokens.sort().join('+')}|${key}`;
}

function acceleratorModifiers(
  value: string | undefined,
  targetPlatform: Platform,
): ChordModifier[] {
  if (!value) return [];
  const modifierByToken: Readonly<Record<string, ChordModifier>> = {
    alt: 'Alt', option: 'Alt', shift: 'Shift',
    command: 'Cmd', cmd: 'Cmd', control: 'Ctrl', ctrl: 'Ctrl',
  };
  const modifiers: ChordModifier[] = [];
  for (const rawToken of value.split('+').slice(0, -1)) {
    const token = rawToken.trim().toLowerCase();
    const resolved = token === 'cmdorctrl' || token === 'commandorcontrol'
      ? (targetPlatform === 'mac' ? 'Cmd' : 'Ctrl')
      : modifierByToken[token];
    if (resolved && !modifiers.includes(resolved)) modifiers.push(resolved);
  }
  return modifiers;
}

function commandFeatureEnabled(
  commandId: KeybindingCommandId,
  target: ResolvedKeybindingTarget | null,
): boolean {
  if (commandId === 'global.importData' && !isBetaFeatureEnabled('data-import')) return false;
  const id = executionId(commandId, commandId);
  const command = getCommandById(id);
  if (!command) return true;
  if (command.isEnabled?.() === false) return false;
  if (target?.scope === 'global' && target.hasHandler(commandId)) return true;
  return command.isVisible?.() !== false;
}

function defaultDependencies(): AppCommandRunnerDependencies {
  const targetPlatform = platform();
  return {
    getActiveTabId: () => useWorkspaceStore.getState().activeTabId,
    getTarget: (commandId, activeTabId) => {
      const definition = defaultKeybindings[commandId];
      return resolveActiveTarget(definition.scope, activeTabId);
    },
    isAllowed: (id, source, target) => isCommandAllowed(id, source, target, {
      isFeatureEnabled: (commandId) => commandFeatureEnabled(commandId, target),
    }),
    hasFocus: (commandId, target, accelerator) => {
      const definition = defaultKeybindings[commandId];
      return resolveFocus({
        commandId,
        policy: definition.focus,
        worksInTerminal: definition.worksInTerminal,
        resolvedModifiers: acceleratorModifiers(accelerator, targetPlatform),
        targetPlatform,
        target,
      });
    },
    isCurrentAccelerator: (commandId, accelerator) => {
      const expected = normalizedAccelerator(accelerator, targetPlatform);
      return getEffectiveKeymap()[commandId].some((chord) => {
        const current = chordToElectronAccelerator(chord, targetPlatform);
        return current !== null && normalizedAccelerator(current, targetPlatform) === expected;
      });
    },
    runShell: runShellAction,
    runPalette: executeCommand,
  };
}

export function createAppCommandRunner(
  dependencies: AppCommandRunnerDependencies,
): (id: string, options: RunAppCommandOptions) => RunAppCommandResult {
  return (id, options) => {
    const commandId = keybindingCommandIdFor(id);
    const activeTabId = dependencies.getActiveTabId();
    const target = commandId ? dependencies.getTarget(commandId, activeTabId) : null;
    if (options.source === 'menu-accelerator') {
      if (!commandId || !options.accelerator ||
          !dependencies.isCurrentAccelerator(commandId, options.accelerator)) {
        return { handled: false };
      }
      if (!dependencies.hasFocus(commandId, target, options.accelerator)) {
        return { handled: false };
      }
    }
    if (!dependencies.isAllowed(id, options.source, target)) return { handled: false };

    const routedId = executionId(id, commandId);
    if (dependencies.runShell(routedId)) return { handled: true };
    if (commandId && target?.run(commandId)) return { handled: true };
    return { handled: dependencies.runPalette(routedId) };
  };
}

const defaultRunner = createAppCommandRunner(defaultDependencies());

export function runAppCommand(
  id: string,
  options: RunAppCommandOptions,
): RunAppCommandResult {
  return defaultRunner(id, options);
}
